// Unified Router - Combines MOPUI and MoceroUI routes into single application
import type { RouteRecordRaw } from 'vue-router';

// MOPUI Components (existing)
import LoginDashboard from '../views/dashboard/DashboardIndex.vue';
import NewsEventsPage from '../views/dashboard/NewsEventsPage.vue';
import CircularsPage from '../views/dashboard/CircularsPage.vue';
import SOPDocumentsPage from '../views/dashboard/SOPDocumentsPage.vue';
import NewsEventDetailPage from '../views/dashboard/NewsEventDetailPage.vue';
import NewsEventFormPage from '../views/dashboard/NewsEventFormPage.vue';
import CircularDetailPage from '../views/dashboard/CircularDetailPage.vue';
import CircularFormPage from '../views/dashboard/CircularFormPage.vue';
import SOPDocumentDetailPage from '../views/dashboard/SOPDocumentDetailPage.vue';
import SOPDocumentFormPage from '../views/dashboard/SOPDocumentFormPage.vue';
import AuthLogin from '../views/auth/LoginView.vue';
import AuthForgotPassword from '../views/auth/ForgotPasswordView.vue';

// Import MOPUI accounts routes
import { accountsRoutes } from '../modules/accounts/router/index';

// Import MoceroUI Components lazily to reduce build memory usage
// CRM Components
const CRMLayout = () => import('../../../MoceroUI/src/modules/crm/components/Layout/Header.vue');
const CRMDashboard = () => import('../../../MoceroUI/src/modules/crm/pages/dashboard/dashboard.vue');
const CRMLogin = () => import('../../../MoceroUI/src/modules/crm/pages/Login/login.vue');

// Inventory Components
const InventoryLayout = () => import('../../../MoceroUI/src/modules/Inventory/components/Layout/Header.vue');
const InventoryMasters = () => import('../../../MoceroUI/src/modules/Inventory/pages/MastersforInventory/MastersforInventory.vue');

// User Management Components
const UserManagementLayout = () => import('../../../MoceroUI/src/modules/userManagement/components/Layout/Header.vue');
const UserManagementDashboard = () => import('../../../MoceroUI/src/modules/userManagement/pages/dashboard.vue');
import { TaskRoutes } from '../modules/TaskManagement/router/router'

import {hrRoutes} from '../modules/hr/router/index'
import { userManagementRoutes } from '../modules/userManagement/router/index'
import { communicationRoutes } from '../modules/communication/router/index'
import { alertEngineRoutes } from '../modules/alertEngine/router/index'
import { crmRoutes } from '../modules/CRM/router/index'
import { inventoryRoutes } from '../modules/Inventory/router/index'

export const unifiedRoutes: RouteRecordRaw[] = [
    {
        path: '/',
        redirect: '/auth/login'
    },

    // Unified Authentication Routes
    {
        path: '/auth',
        children: [
            {
                path: 'login',
                name: 'Login',
                component: AuthLogin // Use MOPUI login as primary
            },
            {
                path: 'forgot-password',
                name: 'ForgotPassword',
                component: AuthForgotPassword
            }
        ]
    },

    // Unified Dashboard
    {
        path: '/dashboard',
        name: 'Dashboard',
        component: LoginDashboard,
        meta: { requiresAuth: true }
    },

    // Dashboard detail pages
    {
        path: '/news-events',
        name: 'NewsEvents',
        component: NewsEventsPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/circulars',
        name: 'Circulars',
        component: CircularsPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/sop-documents',
        name: 'SOPDocuments',
        component: SOPDocumentsPage,
        meta: { requiresAuth: true }
    },

    // News & Events detail + form routes
    {
        path: '/news-events/create',
        name: 'NewsEventCreate',
        component: NewsEventFormPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/news-events/:id',
        name: 'NewsEventDetail',
        component: NewsEventDetailPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/news-events/:id/edit',
        name: 'NewsEventEdit',
        component: NewsEventFormPage,
        meta: { requiresAuth: true }
    },

    // Circulars detail + form routes
    {
        path: '/circulars/create',
        name: 'CircularCreate',
        component: CircularFormPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/circulars/:id',
        name: 'CircularDetail',
        component: CircularDetailPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/circulars/:id/edit',
        name: 'CircularEdit',
        component: CircularFormPage,
        meta: { requiresAuth: true }
    },

    // SOP Documents detail + form routes
    {
        path: '/sop-documents/create',
        name: 'SOPDocumentCreate',
        component: SOPDocumentFormPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/sop-documents/:id',
        name: 'SOPDocumentDetail',
        component: SOPDocumentDetailPage,
        meta: { requiresAuth: true }
    },
    {
        path: '/sop-documents/:id/edit',
        name: 'SOPDocumentEdit',
        component: SOPDocumentFormPage,
        meta: { requiresAuth: true }
    },

    // Direct redirect for /accounts to MOPLayout
    // {
    //     path: '/accounts',
    //     redirect: '/MOPLayout/accounts'
    // },

    // MOPUI Routes (Modern Modules) - All routes go through MOPLayout
    {
        path: '/MOPLayout',
        name: 'MOPLayout',
        component: () => import('../Layout/MOPLayout.vue'),
        meta: { requiresAuth: true, moduleType: 'mopui' },
        children: [
            ...accountsRoutes,
            ...TaskRoutes,
            ...hrRoutes,
            ...userManagementRoutes,
            ...communicationRoutes,
            ...alertEngineRoutes,
            ...userManagementRoutes,
            ...crmRoutes,
            ...inventoryRoutes
        ]
    },

    // MoceroUI Routes (Legacy Modules) - Integrated directly
    {
        path: '/crm',
        name: 'CRMModule',
        component: CRMLayout,
        meta: { requiresAuth: true, moduleType: 'moceroui' },
        children: [
            {
                path: '',
                name: 'CRMModuleDefault',
                redirect: '/crm/dashboard'
            },
            {
                path: 'dashboard',
                name: 'CRMDashboard',
                component: CRMDashboard,
                meta: { requiresAuth: true }
            }
        ]
    },

    // MoceroUI Inventory routes are registered dynamically via registerInventoryRoutes()
    // to avoid eagerly importing 100+ components and causing OOM during build

    {
        path: '/user-management',
        redirect: '/MOPLayout/user-management'
    },
    {
        path: '/alert-engine',
        redirect: '/MOPLayout/alert-engine'
    },

    // ── Alert Engine — legacy MoceroUI URL aliases ──────────────────────────
    // MoceroUI's alertEngine routed each master at its own top-level path
    // (e.g. `/vendorlist`, `/headerList`, `/creditPriceTableView`). Anything
    // pointing at those — bookmarks, backend `FixedHref` values from the menu
    // API — gets bounced to the new MOPUI path. Add a redirect here whenever
    // a new master lands in `modules/alertEngine/router/index.ts`.
    { path: '/vendorlist',             redirect: '/MOPLayout/alert-engine/vendor' },
    { path: '/headerList',             redirect: '/MOPLayout/alert-engine/header' },
    { path: '/channelSubTypeList',     redirect: '/MOPLayout/alert-engine/channel-sub-type' },
    { path: '/creditPlanTblView',      redirect: '/MOPLayout/alert-engine/credit-conversion' },
    { path: '/creditPriceTableView',   redirect: '/MOPLayout/alert-engine/credit-plan' },
    { path: '/messageClassTableview',  redirect: '/MOPLayout/alert-engine/message-class' },
    { path: '/packagetableview',       redirect: '/MOPLayout/alert-engine/package' },
    { path: '/smsTemplateTableview',   redirect: '/MOPLayout/alert-engine/sms-template' },
    // MoceroUI's `/smsTemplateCreation?Id=N` goes to the routed edit page; when
    // there's no Id, send to /create.
    {
      path: '/smsTemplateCreation',
      redirect: (to: any) => to.query.Id
        ? `/MOPLayout/alert-engine/sms-template/edit/${to.query.Id}`
        : '/MOPLayout/alert-engine/sms-template/create'
    },
    { path: '/whatsAppTemplateTableview', redirect: '/MOPLayout/alert-engine/whatsapp-template' },
    {
      path: '/whatsAppTemplateCreation',
      redirect: (to: any) => to.query.Id
        ? `/MOPLayout/alert-engine/whatsapp-template/edit/${to.query.Id}`
        : '/MOPLayout/alert-engine/whatsapp-template/create'
    },
    { path: '/ivrTemplateTableview', redirect: '/MOPLayout/alert-engine/ivr-template' },
    {
      path: '/ivrTemplateCreation',
      redirect: (to: any) => to.query.Id
        ? `/MOPLayout/alert-engine/ivr-template/edit/${to.query.Id}`
        : '/MOPLayout/alert-engine/ivr-template/create'
    },
    { path: '/notificationTemplateTableview', redirect: '/MOPLayout/alert-engine/notification-template' },
    {
      path: '/notificationTemplateCreation',
      redirect: (to: any) => to.query.Id
        ? `/MOPLayout/alert-engine/notification-template/edit/${to.query.Id}`
        : '/MOPLayout/alert-engine/notification-template/create'
    },
    { path: '/emailTemplateTableview', redirect: '/MOPLayout/alert-engine/email-template' },
    {
      path: '/emailTemplateCreation',
      redirect: (to: any) => to.query.Id
        ? `/MOPLayout/alert-engine/email-template/edit/${to.query.Id}`
        : '/MOPLayout/alert-engine/email-template/create'
    },
    { path: '/clientTableview', redirect: '/MOPLayout/alert-engine/client' },
    {
      path: '/clientCreation',
      redirect: (to: any) => to.query.Id
        ? `/MOPLayout/alert-engine/client/edit/${to.query.Id}`
        : '/MOPLayout/alert-engine/client/create'
    },
    // MoceroUI's details views use a query param: /xxxDetailsView?id=N
    {
      path: '/messageClassDetailsView',
      redirect: (to: any) => `/MOPLayout/alert-engine/message-class/view/${to.query.id ?? 0}`
    },
    {
      path: '/packageMasterDetailsView',
      redirect: (to: any) => `/MOPLayout/alert-engine/package/view/${to.query.Id ?? to.query.id ?? 0}`
    },
    { path: '/Masters',                redirect: '/MOPLayout/alert-engine/dashboard' },

    // ── CRM — legacy MoceroUI URL aliases ───────────────────────────────────
    // MoceroUI's CRM used top-level paths (e.g. `/taskDetails`, `/appointment`,
    // `/contactListview`). Anything still pointing at those — the embedded
    // MoceroUI CRM header, bookmarks, or backend `FixedHref` menu values — has
    // no matching route in MOPUI and falls through to the login page. Bounce
    // them to the equivalent new MOPUI CRM page so they open instead.
    { path: '/taskDetails',      redirect: '/MOPLayout/crm/tasks' },
    { path: '/newTaskCreation',  redirect: '/MOPLayout/crm/tasks' },
    { path: '/contactListview',  redirect: '/MOPLayout/crm/contacts' },
    { path: '/contacttableview', redirect: '/MOPLayout/crm/contacts' },
    { path: '/contactDetail',    redirect: (to: any) => ({ path: '/MOPLayout/crm/contacts', query: to.query }) },
    { path: '/callLog',          redirect: '/MOPLayout/crm/call-logs' },
    { path: '/webWhatsapp',      redirect: '/MOPLayout/crm/whatsapp' },
    { path: '/campaignTable',    redirect: '/MOPLayout/crm/campaigns' },
    { path: '/crmmasters',       redirect: '/MOPLayout/crm/masters' },
    // Appointment opens the external booking app in a frame — keep the PatientId.
    { path: '/appointment',      redirect: (to: any) => ({ path: '/MOPLayout/crm/appointment', query: to.query }) },

    // Legacy route redirects for backward compatibility
    {
        path: '/Layout/dashboard',
        redirect: '/crm/dashboard'
    },
    {
        path: '/InventoryLayout',
        redirect: '/inventory'
    },
    {
        path: '/InventoryLayout/Masters',
        redirect: '/inventory/masters'
    },
    {
        path: '/billingLayout/userdashboard',
        redirect: '/MOPLayout/user-management'
    }
];
